import { CONSTANTS } from '../../_data/lookups.js';
import { getBranchPatternWindow, readMaterializedBooleanDecision, } from './routingHelpers.js';
export function shouldRouteToBranchExitRebindAtBoundary(args) {
    const { step, threadSteps, currentStepCounter, rebindGuard, toolMap, workflowState, } = args;
    if (rebindGuard === currentStepCounter) {
        return false;
    }
    // Boundary rebind applies when the prior executed pattern is BRANCH -> WHEN(tool) -> WHAT(tool)
    // and the current step is the first non-branch boundary after that execution.
    const { prev, prevPrev, prevPrevPrev } = getBranchPatternWindow(threadSteps, currentStepCounter);
    if (step.stepKind === CONSTANTS.Enums.StepKind.branch ||
        !prev ||
        !prevPrev ||
        !prevPrevPrev ||
        prev.stepKind !== CONSTANTS.Enums.StepKind.tool ||
        prevPrev.stepKind !== CONSTANTS.Enums.StepKind.tool ||
        prevPrevPrev.stepKind !== CONSTANTS.Enums.StepKind.branch) {
        return false;
    }
    const whenToolStep = prevPrev;
    return readMaterializedBooleanDecision(workflowState, whenToolStep, toolMap);
}
export function getToolStepShortCircuitDecision(args) {
    const { step, threadSteps, currentStepCounter, currentThreadIndex, toolMap, workflowState, } = args;
    if (step.stepKind !== CONSTANTS.Enums.StepKind.tool) {
        return { toolStepShortCircuitDecisionKind: 'none' };
    }
    // Short-circuit only applies at the WHAT(tool) position in [BRANCH, WHEN(tool), WHAT(tool)].
    const { prev, prevPrev } = getBranchPatternWindow(threadSteps, currentStepCounter);
    if (!prev ||
        !prevPrev ||
        prev.stepKind !== CONSTANTS.Enums.StepKind.tool ||
        prevPrev.stepKind !== CONSTANTS.Enums.StepKind.branch) {
        return { toolStepShortCircuitDecisionKind: 'none' };
    }
    const whenToolStep = prev;
    const decision = readMaterializedBooleanDecision(workflowState, whenToolStep, toolMap);
    if (decision) {
        return { toolStepShortCircuitDecisionKind: 'none' };
    }
    const nextIndex = currentStepCounter + 1;
    if (nextIndex >= threadSteps.length) {
        return { toolStepShortCircuitDecisionKind: 'end' };
    }
    const nextStep = threadSteps[nextIndex];
    if (!nextStep) {
        throw new Error(`Next step not found for thread ${currentThreadIndex} at counter ${nextIndex}`);
    }
    return {
        toolStepShortCircuitDecisionKind: 'skipToIndex',
        nextIndex,
    };
}
export function shouldRouteBranchStepToExitRebind(args) {
    const { step, threadSteps, currentStepCounter, rootBranchStepKeyByRemainderBranchStepKey, toolMap, workflowState, } = args;
    if (step.stepKind !== CONSTANTS.Enums.StepKind.branch) {
        return false;
    }
    const { prev, prevPrev, prevPrevPrev } = getBranchPatternWindow(threadSteps, currentStepCounter);
    if (!prev ||
        !prevPrev ||
        !prevPrevPrev ||
        prev.stepKind !== CONSTANTS.Enums.StepKind.tool ||
        prevPrev.stepKind !== CONSTANTS.Enums.StepKind.tool ||
        prevPrevPrev.stepKind !== CONSTANTS.Enums.StepKind.branch) {
        return false;
    }
    const whenToolStep = prevPrev;
    if (!readMaterializedBooleanDecision(workflowState, whenToolStep, toolMap)) {
        return false;
    }
    const currentBranchStepKey = step.stepKey;
    const previousBranchStepKey = prevPrevPrev.stepKey;
    const currentRootBranchStepKey = rootBranchStepKeyByRemainderBranchStepKey[currentBranchStepKey] ?? currentBranchStepKey;
    const previousRootBranchStepKey = rootBranchStepKeyByRemainderBranchStepKey[previousBranchStepKey] ?? previousBranchStepKey;
    if (currentRootBranchStepKey !== previousRootBranchStepKey) {
        return false;
    }
    // Current branch must be a remainder (not the root branch itself).
    if (currentBranchStepKey === currentRootBranchStepKey) {
        return false;
    }
    return true;
}
